import { __awaiter } from "tslib";
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from '../types';
import { MISSING_CUSTOMER } from '@open-tender/types';
import { checkAuth, selectToken } from './account';
import { showNotification } from '../notifications';
const initialState = {
    entities: [],
    lookup: {},
    loading: 'idle',
    error: null
};
export var CustomerCreditCardsActionType;
(function (CustomerCreditCardsActionType) {
    CustomerCreditCardsActionType["FetchCustomerCreditCards"] = "customer/fetchCustomerCreditCards";
    CustomerCreditCardsActionType["UpdateCustomerCreditCard"] = "customer/updateCustomerCreditCard";
    CustomerCreditCardsActionType["RemoveCustomerCreditCard"] = "customer/removeCustomerCreditCard";
    CustomerCreditCardsActionType["AddCustomerCreditCard"] = "customer/addCustomerCreditCard";
})(CustomerCreditCardsActionType || (CustomerCreditCardsActionType = {}));
export const fetchCustomerCreditCards = createAsyncThunk(CustomerCreditCardsActionType.FetchCustomerCreditCards, (includeLinked, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const creditCards = yield api.getCustomerCreditCards(token, includeLinked);
        return creditCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const updateCustomerCreditCard = createAsyncThunk(CustomerCreditCardsActionType.UpdateCustomerCreditCard, ({ cardId, data, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.putCustomerCreditCard(token, cardId, data);
        const includeLinked = true;
        const creditCards = yield api.getCustomerCreditCards(token, includeLinked);
        dispatch(showNotification('Credit card updated!'));
        if (callback)
            callback();
        return creditCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const removeCustomerCreditCard = createAsyncThunk(CustomerCreditCardsActionType.RemoveCustomerCreditCard, ({ cardId, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.deleteCustomerCreditCard(token, cardId);
        const includeLinked = true;
        const creditCards = yield api.getCustomerCreditCards(token, includeLinked);
        dispatch(showNotification('Credit card removed!'));
        if (callback)
            callback();
        return creditCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const addCustomerCreditCard = createAsyncThunk(CustomerCreditCardsActionType.AddCustomerCreditCard, ({ data, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.postCustomerCreditCard(token, data);
        const includeLinked = true;
        const creditCards = yield api.getCustomerCreditCards(token, includeLinked);
        dispatch(showNotification('Credit card added!'));
        if (callback)
            callback();
        return creditCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
const customerCreditCardsSlice = createSlice({
    name: ReducerType.CreditCards,
    initialState,
    reducers: {
        resetCustomerCreditCards: () => initialState,
        resetCustomerCreditCardsError: state => {
            state.error = null;
            state.loading = 'idle';
        },
        setCustomerCreditCards: (state, action) => {
            state.entities = action.payload;
            state.error = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchCustomerCreditCards.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchCustomerCreditCards.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchCustomerCreditCards.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(updateCustomerCreditCard.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(updateCustomerCreditCard.pending, state => {
            state.loading = 'pending';
        })
            .addCase(updateCustomerCreditCard.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(removeCustomerCreditCard.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(removeCustomerCreditCard.pending, state => {
            state.loading = 'pending';
        })
            .addCase(removeCustomerCreditCard.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(addCustomerCreditCard.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(addCustomerCreditCard.pending, state => {
            state.loading = 'pending';
        })
            .addCase(addCustomerCreditCard.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        });
    }
});
export const { resetCustomerCreditCards, resetCustomerCreditCardsError, setCustomerCreditCards } = customerCreditCardsSlice.actions;
export const selectCustomerCreditCards = (state) => state.customer.creditCards;
export const selectCustomerCreditCardsForPayment = createSelector((state) => {
    const creditCards = state.customer.creditCards.entities;
    return creditCards;
}, creditCards => {
    return creditCards.filter(i => i.has_profile);
});
export const customerCreditCardsReducer = customerCreditCardsSlice.reducer;
